import { HelixIcon } from '@helix/helix-icon'
import { chevron_right as ChevronRightIcon } from '@helix/helix-icon/outlined'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  ElementType,
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  ReactNode
} from 'react'
import {
  PolymorphicForwardRefExoticComponent,
  PolymorphicPropsWithoutRef
} from './polymorphic'
import { classNames } from './utils'

interface MenuProps {
  button: ReactNode
  direction?: 'left' | 'center' | 'right'
  disabled?: boolean
}

export function Menu({
  direction = 'right',
  disabled = false,
  button,
  children
}: PropsWithChildren<MenuProps>) {
  let side: 'top' | 'right' | 'bottom' | 'left' = 'bottom'
  let align: 'start' | 'center' | 'end' = 'end'

  switch (direction) {
    case 'left':
      side = 'bottom'
      align = 'start'
      break
    case 'center':
      side = 'bottom'
      align = 'center'
      break
    case 'right':
    default:
      side = 'bottom'
      align = 'end'
  }

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger
        asChild
        disabled={disabled}
        className="disabled:cursor-default">
        <button
          disabled={disabled}
          className="flex items-center rounded outline-none focus:ring-2 focus:ring-brand-primary disabled:cursor-default"
          aria-label="Open menu">
          <span className="sr-only">Open menu</span>
          {button}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          align={align}
          sideOffset={4}
          className={classNames(
            'z-10 mt-2 overflow-hidden whitespace-nowrap rounded-md bg-white p-2 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
            {
              'origin-top-left': direction === 'left',
              'origin-top-center left-1/2 -translate-x-1/2':
                direction === 'center',
              'origin-top-right': direction === 'right'
            }
          )}>
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

const defaultElement = 'button'

interface MenuItemProps {
  icon?: ReactNode
  variant?: 'default' | 'danger'
  disabled?: boolean
  active?: boolean
  submenu?: ReactNode
}

const MenuItem: PolymorphicForwardRefExoticComponent<
  MenuItemProps,
  typeof defaultElement
> = forwardRef(function MenuItem<T extends ElementType = typeof defaultElement>(
  {
    as,
    variant = 'default',
    active: controlledActive,
    disabled,
    children,
    icon,
    submenu,
    ...props
  }: PolymorphicPropsWithoutRef<MenuItemProps, T>,
  ref: ForwardedRef<Element>
) {
  if (props.className) {
    throw new Error('You cannot add classNames to the menu item')
  }

  const Element: ElementType = as || defaultElement

  if (submenu) {
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger asChild disabled={disabled}>
          <Element
            ref={ref}
            {...props}
            className={classNames(
              'flex w-full items-center justify-between space-x-8 rounded-md px-4 py-2 text-left text-sm hover:bg-gray-100 hover:outline-none',
              {
                'bg-gray-100 text-gray-900':
                  controlledActive && variant === 'default',
                'text-gray-900': !controlledActive && variant === 'default',

                'bg-red-100 text-red-900':
                  controlledActive && variant === 'danger',
                'text-red-700': !controlledActive && variant === 'danger',

                // Only dim the item if we aren't controlling active
                'opacity-50': disabled && !controlledActive,
                'cursor-not-allowed': disabled
              }
            )}>
            <span>{children}</span>
            <span
              className={classNames('h-4 w-4 text-gray-600', {
                'text-red-900': controlledActive && variant === 'danger',
                'text-red-700': !controlledActive && variant === 'danger'
              })}>
              <HelixIcon
                icon={ChevronRightIcon}
                className="h-4 w-4 text-gray-600"
              />
            </span>
          </Element>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.Portal>
          <DropdownMenu.SubContent
            sideOffset={4}
            alignOffset={-4}
            className="z-10 mt-1 rounded-md bg-white p-2 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {submenu}
          </DropdownMenu.SubContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Sub>
    )
  }

  return (
    <DropdownMenu.Item asChild disabled={disabled}>
      <Element
        ref={ref}
        {...props}
        className={classNames(
          'flex w-full items-center justify-between space-x-8 rounded-md px-4 py-2 text-left text-sm hover:bg-gray-100 hover:outline-none',
          {
            'bg-gray-100 text-gray-900':
              controlledActive && variant === 'default',
            'text-gray-900': !controlledActive && variant === 'default',

            'bg-red-100 text-red-900': controlledActive && variant === 'danger',
            'text-red-700': !controlledActive && variant === 'danger',

            // Only dim the item if we aren't controlling active
            'opacity-50': disabled && !controlledActive,
            'cursor-not-allowed': disabled
          }
        )}>
        <span>{children}</span>
        <span
          className={classNames('h-4 w-4 text-gray-600', {
            'text-red-900': controlledActive && variant === 'danger',
            'text-red-700': !controlledActive && variant === 'danger'
          })}>
          {icon}
        </span>
      </Element>
    </DropdownMenu.Item>
  )
})

Menu.Item = MenuItem
